import { httpClient } from "@/features/auth/session";
import { isMaintenanceError, ApiError } from "@/lib/errors";
import {
  MAINTENANCE_OFF,
  getMaintenance,
  isClosed,
  statusFromResponse,
  type MaintenanceStatus,
} from "./maintenanceStore";

/**
 * Asks the API whether maintenance is on. It needs no session, so the login
 * page can ask too. It resolves to null when the answer is unknown (the API
 * cannot be reached, or it sent something unexpected): that says nothing about
 * maintenance, so the caller keeps what it knew.
 */
export async function fetchMaintenanceStatus(signal?: AbortSignal): Promise<MaintenanceStatus | null> {
  try {
    const data = await httpClient.request<unknown>("GET", "/api/v1/maintenance", undefined, {
      auth: false,
      unwrapData: true,
      timeoutMs: 8_000,
      signal,
    });
    return statusFromResponse(data);
  } catch {
    return null;
  }
}

/**
 * Finds out whether the API is open again by making one real read, for when the
 * status route cannot say (an older API without it, or an outage of that route
 * alone). Without this the maintenance page could never clear. Any answer but
 * MAINTENANCE, even a 401, means the API is serving again. The call is made
 * without session recovery, so it can neither sign in nor out.
 */
export async function probeMaintenanceOver(signal?: AbortSignal): Promise<MaintenanceStatus | null> {
  try {
    await httpClient.request("GET", "/api/v1/me", undefined, { auth: false, timeoutMs: 8_000, signal });
    return MAINTENANCE_OFF;
  } catch (error) {
    if (isMaintenanceError(error)) return null;
    // A 4xx is an answer from a working API; a network failure is not.
    return error instanceof ApiError && error.status < 500 ? MAINTENANCE_OFF : null;
  }
}

/**
 * The status as best it can be known: the status route first, then, only while
 * the app is closed and the route could not answer, a real read.
 */
export async function refreshMaintenanceStatus(signal?: AbortSignal): Promise<MaintenanceStatus | null> {
  const status = await fetchMaintenanceStatus(signal);
  if (status) return status;
  return isClosed(getMaintenance()) ? probeMaintenanceOver(signal) : null;
}
